import React from "react";
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import Tooltip from "@material-ui/core/Tooltip";
import {makeStyles} from "@material-ui/core/styles";
import * as colors from "material-ui/styles/colors";

import requestor from "requestor";
import clausePreviewsFetchAction from "modules/documents/actions/clause_previews_fetch";
import clausePreviewKey from "modules/documents/utils/clause_preview_key";
import getFamilyDocumentIds from "modules/documents/utils/get_family_document_ids";
import {ClausePreviewEntry} from "modules/documents/reducers/clause_previews";
import {OtherDocumentClause} from "common/types/clause_preview";
import {OrganisationId} from "common/types/organisation";
import {ProjectId} from "common/types/project";
import {DocumentId} from "common/types/document";

const MAX_PREVIEW_LENGTH = 400;

const linkStyle: React.CSSProperties = {
  textDecoration: "underline dotted",
  cursor: "help",
};

const useStyles = makeStyles({
  tooltip: {
    maxWidth: 480,
    padding: "10px 14px",
    backgroundColor: "white",
    color: colors.grey800,
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 400,
    border: `1px solid ${colors.grey300}`,
    borderRadius: 4,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
  },
  arrow: {
    color: "white",
    "&::before": {
      border: `1px solid ${colors.grey300}`,
    },
  },
  documentName: {
    fontWeight: 600,
    color: colors.grey600,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 6,
  },
});

interface OwnProps {
  text: string;
  clause: OtherDocumentClause;
  organisationId: OrganisationId;
  projectId: ProjectId;
}

interface OtherDocumentClauseLinkProps extends OwnProps {
  entry?: ClausePreviewEntry;
  isInFamily: boolean;
  fetchClausePreviews: (
    organisationId: OrganisationId,
    projectId: ProjectId,
    documentId: DocumentId,
    clauses: OtherDocumentClause[],
  ) => void;
}

function truncate(text: string) {
  return text.length > MAX_PREVIEW_LENGTH
    ? `${text.slice(0, MAX_PREVIEW_LENGTH)}…`
    : text;
}

function OtherDocumentClauseLink({
  text,
  clause,
  organisationId,
  projectId,
  entry,
  isInFamily,
  fetchClausePreviews,
}: OtherDocumentClauseLinkProps) {
  const classes = useStyles();

  // No preview for unknown documents
  if (!isInFamily) {
    return <span>{text}</span>;
  }

  const load = () => {
    if (!entry || entry.status === "failed") {
      fetchClausePreviews(organisationId, projectId, clause.document_id, [
        clause,
      ]);
    }
  };

  const loaded = entry?.status === "loaded" ? entry.preview : undefined;
  const previewText = loaded ? loaded.text ?? clause.clause_text : undefined;
  const title =
    !entry || entry.status === "loading" ? (
      "Loading…"
    ) : !loaded || !previewText ? (
      "Preview unavailable"
    ) : (
      <div>
        <div className={classes.documentName}>{loaded.document_name}</div>
        <div>{truncate(previewText)}</div>
      </div>
    );

  return (
    <Tooltip
      title={title}
      onOpen={load}
      arrow
      classes={{tooltip: classes.tooltip, arrow: classes.arrow}}
    >
      <span style={linkStyle}>{text}</span>
    </Tooltip>
  );
}

function select(state, ownProps: OwnProps) {
  return {
    entry: state.clausePreviews[clausePreviewKey(ownProps.clause)],
    isInFamily: Boolean(
      getFamilyDocumentIds(state.matter)?.includes(ownProps.clause.document_id),
    ),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {fetchClausePreviews: clausePreviewsFetchAction(requestor)},
    dispatch,
  );
}

export default connect(select, mapDispatchToProps)(OtherDocumentClauseLink);
